import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './CarteiraDashboard.css';
import { getPortfolio, removePosition, addPosition, closePosition, getRiskSummary } from '../api/portfolio';
import PortfolioModal from './PortfolioModal';
import PortfolioPositionChart from './PortfolioPositionChart';
import PortfolioEquityChart from './PortfolioEquityChart';
import { formatDateTimeBR, formatTradingDate } from '../utils/dates';
import { normalizeStrategyId, strategyLabel } from '../utils/strategies';

// ── Formatters ──────────────────────────────────────────
const fmtBRL = (v, showSign = false) => {
  if (v == null || isNaN(v)) return '—';
  const sign = showSign && v > 0 ? '+' : '';
  return sign + 'R$\u202F' + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtPct = (v) => {
  if (v == null || isNaN(v)) return '—';
  const s = v > 0 ? '+' : '';
  return `${s}${v.toFixed(2)}%`;
};
const fmtSignedBRL = (v) => {
  if (v == null || isNaN(v)) return '—';
  const sign = v > 0 ? '+' : v < 0 ? '-' : '';
  return sign + 'R$\u202F' + Math.abs(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtBrtMoment = (value) => {
  return value ? formatDateTimeBR(value) : null;
};

// ── Strategy colours ─────────────────────────────────────
const STRAT_COLORS = {
  'RSI(2) Connors':      { color: '#26a69a', bg: 'rgba(38,166,154,0.13)',  border: 'rgba(38,166,154,0.25)' },
  'Kairi + Confluência': { color: '#818cf8', bg: 'rgba(129,140,248,0.13)', border: 'rgba(129,140,248,0.25)' },
  'Kairi + Confluência (Intraday)': { color: '#38bdf8', bg: 'rgba(56,189,248,0.13)', border: 'rgba(56,189,248,0.28)' },
  'Scanner B3':          { color: '#facc15', bg: 'rgba(250,204,21,0.11)',  border: 'rgba(250,204,21,0.22)' },
  'Reversão na Tendência': { color: '#65b6e7', bg: 'rgba(101,182,231,0.13)', border: 'rgba(101,182,231,0.28)' },
  'Análise Setorial':    { color: '#a78bfa', bg: 'rgba(167,139,250,0.13)', border: 'rgba(167,139,250,0.28)' },
  'Manual':              { color: '#9ba3b8', bg: 'rgba(255,255,255,0.07)', border: 'rgba(255,255,255,0.12)' },
};
function sc(s) { return STRAT_COLORS[strategyLabel(s)] || { color: '#9ba3b8', bg: 'rgba(255,255,255,0.07)', border: 'rgba(255,255,255,0.12)' }; }

// ── P&L Waterfall Chart ──────────────────────────────────
function PnLChart({ positions }) {
  const withPnl = useMemo(
    () => [...positions].filter(p => p.pnl_r != null).sort((a, b) => (b.pnl_r || 0) - (a.pnl_r || 0)),
    [positions]
  );

  if (!withPnl.length) {
    return (
      <div className="cart-chart-empty">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" opacity="0.25">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
        </svg>
        Aguardando dados de preço
      </div>
    );
  }

  const W = 580; const H = 190;
  const PL = 54; const PR = 12; const PT = 14; const PB = 34;
  const iW = W - PL - PR; const iH = H - PT - PB;

  const maxAbs = Math.max(...withPnl.map(p => Math.abs(p.pnl_r)), 0.01);
  const scale  = maxAbs * 1.18;
  const yMid   = PT + iH / 2;
  const toY    = (v) => yMid - (v / scale) * (iH / 2);

  const n    = withPnl.length;
  const gap  = iW / n;
  const bW   = Math.max(6, Math.min(36, gap * 0.65));
  const xAt  = (i) => PL + i * gap + gap / 2;

  // Cumulative equity line
  let cum = 0;
  const cumPts = withPnl.map((p, i) => {
    cum += p.pnl_r || 0;
    return { x: xAt(i), y: toY(cum) };
  });
  const lineD = cumPts.map((pt, i) => `${i === 0 ? 'M' : 'L'}${pt.x.toFixed(1)},${pt.y.toFixed(1)}`).join(' ');
  const areaD = `${lineD} L${cumPts[cumPts.length - 1].x.toFixed(1)},${yMid.toFixed(1)} L${cumPts[0].x.toFixed(1)},${yMid.toFixed(1)} Z`;

  const totalPnl = withPnl.reduce((s, p) => s + (p.pnl_r || 0), 0);

  return (
    <div className="cart-chart-wrap">
      <div className="cart-chart-header">
          <span className="cart-chart-title">Resultados realizados</span>
        <div className="cart-chart-line-legend">
          <span className="cart-chart-legend-dot" style={{ background: '#5b9dff' }} />
          <span>Curva acumulada</span>
        </div>
        <span className={`cart-chart-total ${totalPnl >= 0 ? 'pos' : 'neg'}`}>
          {fmtSignedBRL(totalPnl)} total
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="cart-chart-svg" preserveAspectRatio="none">
        {/* Grid lines */}
        {[-1, -0.5, 0, 0.5, 1].map((frac, i) => {
          const y = yMid - frac * iH / 2;
          return (
            <g key={i}>
              <line x1={PL} y1={y.toFixed(1)} x2={W - PR} y2={y.toFixed(1)}
                stroke={frac === 0 ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)'}
                strokeWidth={frac === 0 ? 1 : 0.5}
                strokeDasharray={frac === 0 ? '' : '3,4'}
              />
              {frac !== 0 && (
                <text x={PL - 4} y={(y + 3.5).toFixed(1)} fill="#2d3a50" fontSize="8" textAnchor="end" fontFamily="'JetBrains Mono',monospace">
                  {fmtSignedBRL(scale * frac)}
                </text>
              )}
            </g>
          );
        })}
        <text x={PL - 4} y={(yMid + 3.5).toFixed(1)} fill="#3d4860" fontSize="8" textAnchor="end" fontFamily="'JetBrains Mono',monospace">0</text>

        {/* Bars */}
        {withPnl.map((p, i) => {
          const v    = p.pnl_r || 0;
          const isP  = v >= 0;
          const bH   = Math.max(2, Math.abs(v) / scale * iH / 2);
          const bX   = xAt(i) - bW / 2;
          const bY   = isP ? yMid - bH : yMid;
          const col  = isP ? '#26a69a' : '#ef5350';
          const isBest  = withPnl[0]?.id === p.id;
          const isWorst = withPnl[withPnl.length - 1]?.id === p.id;
          return (
            <g key={p.id}>
              <rect x={bX.toFixed(1)} y={bY.toFixed(1)} width={bW.toFixed(1)} height={bH.toFixed(1)}
                fill={col} opacity={isBest ? 1 : isWorst ? 0.9 : 0.78} rx="2" />
              {isBest  && <rect x={(bX - 1).toFixed(1)} y={(bY - 1).toFixed(1)} width={(bW + 2).toFixed(1)} height={(bH + 1).toFixed(1)} fill="none" stroke="#26a69a" strokeWidth="1" opacity="0.5" rx="2" />}
              {isWorst && <rect x={(bX - 1).toFixed(1)} y={(bY - 1).toFixed(1)} width={(bW + 2).toFixed(1)} height={(bH + 1).toFixed(1)} fill="none" stroke="#ef5350" strokeWidth="1" opacity="0.5" rx="2" />}
              <text x={xAt(i).toFixed(1)} y={(H - PB + 13).toFixed(1)} fill="#4a5a75" fontSize="8.5"
                textAnchor="middle" fontFamily="'JetBrains Mono',monospace" fontWeight="600">
                {p.ticker}
              </text>
              <text x={xAt(i).toFixed(1)} y={(H - PB + 23).toFixed(1)} fill={isP ? '#26a69a' : '#ef5350'} fontSize="7.5"
                textAnchor="middle" fontFamily="'JetBrains Mono',monospace">
                {v >= 0 ? '+' : ''}{v.toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Area under cumulative line */}
        <path d={areaD} fill="url(#cumGrad)" opacity="0.18" />
        {/* Cumulative line */}
        <path d={lineD} fill="none" stroke="#5b9dff" strokeWidth="1.8" strokeLinejoin="round" />
        {/* Dots on cumulative line */}
        {cumPts.map((pt, i) => (
          <circle key={i} cx={pt.x.toFixed(1)} cy={pt.y.toFixed(1)} r="2.8" fill="#5b9dff" stroke="#0d1120" strokeWidth="1" />
        ))}

        <defs>
          <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#5b9dff" />
            <stop offset="100%" stopColor="#5b9dff" stopOpacity="0" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  );
}

// ── Allocation Donut ─────────────────────────────────────
function AllocationDonut({ positions }) {
  const slices = useMemo(() => {
    const map = {};
    positions.forEach(p => {
      const key = p.strategy || 'Outros';
      map[key] = (map[key] || 0) + p.entry_price * p.quantity;
    });
    const total = Object.values(map).reduce((s, v) => s + v, 0) || 1;
    const palette = ['#26a69a', '#818cf8', '#facc15', '#5b9dff', '#f59e0b'];
    return Object.entries(map).map(([label, val], i) => ({
      label, val, pct: val / total, color: palette[i % palette.length],
    }));
  }, [positions]);

  if (!slices.length) return null;

  const R  = 38; const r = 22;
  const CX = 46; const CY = 46;
  let angle = -Math.PI / 2;

  const paths = slices.map(s => {
    const sweep = s.pct * 2 * Math.PI;
    const x1 = CX + R * Math.cos(angle);
    const y1 = CY + R * Math.sin(angle);
    angle += sweep;
    const x2 = CX + R * Math.cos(angle);
    const y2 = CY + R * Math.sin(angle);
    const large = sweep > Math.PI ? 1 : 0;
    return { ...s, d: `M${CX},${CY} L${x1.toFixed(2)},${y1.toFixed(2)} A${R},${R} 0 ${large},1 ${x2.toFixed(2)},${y2.toFixed(2)} Z` };
  });

  return (
    <div className="cart-donut-wrap">
      <div className="cart-donut-title">Alocação</div>
      <div className="cart-donut-body">
        <svg viewBox="0 0 92 92" width="84" height="84">
          {paths.map((s, i) => <path key={i} d={s.d} fill={s.color} opacity="0.88" />)}
          <circle cx={CX} cy={CY} r={r} fill="#0d1120" />
          <text x={CX} y={CY - 3} textAnchor="middle" fill="#e8eaf0" fontSize="11" fontWeight="700">{positions.length}</text>
          <text x={CX} y={CY + 9} textAnchor="middle" fill="#5e6a85" fontSize="7">posições</text>
        </svg>
        <div className="cart-donut-legend">
          {paths.map((s, i) => (
            <div key={i} className="cart-donut-item">
              <span className="cart-donut-dot" style={{ background: s.color }} />
              <span className="cart-donut-label">{s.label}</span>
              <span className="cart-donut-pct">{(s.pct * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StrategyEvolution({ positions, selectedStrategy, onSelect }) {
  const rows = useMemo(() => {
    const grouped = new Map();
    positions.forEach(position => {
      const strategy = position.strategy || 'Manual';
      if (!grouped.has(strategy)) {
        grouped.set(strategy, {
          strategy,
          capital: 0,
          realizedPnl: 0,
          openPnl: 0,
          openPnlMeasured: 0,
          pnl: 0,
          open: 0,
          closed: 0,
          measured: 0,
          winners: 0,
           automatic: { pnl: 0, count: 0, open: 0, closed: 0, winners: 0, measured: 0 },
           override: { pnl: 0, count: 0, open: 0, closed: 0, winners: 0, measured: 0 },
        });
      }
      const item = grouped.get(strategy);
       const bucket = position.override === true
         ? item.override
         : position.automatic_monitoring === true ? item.automatic : null;
      const isClosed = position.status === 'closed';
      const isOpen = position.status === 'open';
      if (isOpen) item.capital += position.entry_price * position.quantity;
      item.open += isOpen ? 1 : 0;
      item.closed += isClosed ? 1 : 0;
       if (bucket) {
         bucket.count += 1;
         bucket.open += isOpen ? 1 : 0;
         bucket.closed += isClosed ? 1 : 0;
       }
      if (isClosed && position.pnl_r != null) {
        item.realizedPnl += position.pnl_r;
        item.measured += 1;
        item.winners += position.pnl_pct > 0 ? 1 : 0;
          if (bucket) {
            bucket.pnl += position.pnl_r;
            bucket.measured += 1;
            bucket.winners += position.pnl_pct > 0 ? 1 : 0;
          }
      }
      if (isOpen && position.pnl_r != null) {
        item.openPnl += position.pnl_r;
        item.openPnlMeasured += 1;
        if (bucket) bucket.pnl += position.pnl_r;
      }
    });
    return [...grouped.values()]
      .map(item => {
        const totalPnl = item.realizedPnl + item.openPnl;
        return {
          ...item,
          pnl: totalPnl,
          returnPct: item.capital ? totalPnl / item.capital * 100 : null,
          winRate: item.measured ? item.winners / item.measured * 100 : null,
           automatic: {
             ...item.automatic,
             winRate: item.automatic.measured ? item.automatic.winners / item.automatic.measured * 100 : null,
           },
           override: {
             ...item.override,
             winRate: item.override.measured ? item.override.winners / item.override.measured * 100 : null,
           },
        };
      })
      .sort((a, b) => b.capital - a.capital);
  }, [positions]);

  if (!rows.length) return null;

  return (
    <section className="cart-strategy-evolution" aria-label="Evolução por estratégia">
      <div className="cart-strategy-head">
        <div>
          <span className="cart-strategy-title">Evolução por estratégia</span>
          <span className="cart-strategy-subtitle">Resultado total: realizado + em aberto</span>
        </div>
        <button
          className={`cart-strategy-filter ${selectedStrategy === 'all' ? 'active' : ''}`}
          onClick={() => onSelect('all')}
        >
          Consolidado
        </button>
      </div>
      <div className="cart-strategy-grid">
        {rows.map(row => {
          const tone = sc(row.strategy);
          const positive = row.pnl >= 0;
          return (
            <button
              key={row.strategy}
              className={`cart-strategy-card ${selectedStrategy === row.strategy ? 'active' : ''}`}
              style={{ '--strategy-color': tone.color, '--strategy-bg': tone.bg, '--strategy-border': tone.border }}
              onClick={() => onSelect(row.strategy)}
            >
              <span className="cart-strategy-name">{strategyLabel(row.strategy)}</span>
              <span className={`cart-strategy-pnl ${positive ? 'pos' : 'neg'}`}>
                {fmtSignedBRL(row.pnl)}
              </span>
              <span className={`cart-strategy-return ${row.returnPct != null && row.returnPct >= 0 ? 'pos' : 'neg'}`}>
                {fmtPct(row.returnPct)} retorno
              </span>
              <span className="cart-strategy-meta">
                Em aberto {row.openPnlMeasured === row.open && row.open > 0 ? fmtSignedBRL(row.openPnl) : '—'} · Realizado {fmtSignedBRL(row.realizedPnl)}
              </span>
              <span className="cart-strategy-meta">
                {row.open} aberta{row.open !== 1 ? 's' : ''} · {row.closed} encerrada{row.closed !== 1 ? 's' : ''}
              </span>
              <span className="cart-strategy-meta">
                Capital {fmtBRL(row.capital)} · Acerto {row.winRate != null ? `${row.winRate.toFixed(0)}%` : '—'}
              </span>
              <span className="cart-strategy-breakdown" aria-label="Separação entre resultado automático e override">
                <span className="cart-strategy-breakdown-label">Automático</span>
                <strong className={row.automatic.pnl >= 0 ? 'pos' : 'neg'}>{fmtSignedBRL(row.automatic.pnl)}</strong>
                <span>{row.automatic.count} pos. · {row.automatic.winRate != null ? `${row.automatic.winRate.toFixed(0)}% acerto` : 'acerto —'}</span>
                <span className="cart-strategy-breakdown-label cart-strategy-breakdown-override">Override</span>
                <strong className={row.override.pnl >= 0 ? 'pos' : 'neg'}>{fmtSignedBRL(row.override.pnl)}</strong>
                <span>{row.override.count} pos. · {row.override.winRate != null ? `${row.override.winRate.toFixed(0)}% acerto` : 'acerto —'}</span>
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

// ── Close Modal ───────────────────────────────────────────
function CloseModal({ pos, onClose, onSave }) {
  const today = new Date().toISOString().split('T')[0];
  const [form, setForm] = useState({
    exit_price:    pos.current_price ? Number(pos.current_price).toFixed(2) : '',
    exit_quantity: String(pos.quantity),
    exit_date:     today,
  });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const strat = sc(pos.strategy);

  const pnlPreview = useMemo(() => {
    const e = parseFloat(form.exit_price);
    const q = parseInt(form.exit_quantity) || pos.quantity;
    if (!e || isNaN(e)) return null;
    const pct = ((e - pos.entry_price) / pos.entry_price) * 100;
    const r   = (e - pos.entry_price) * q;
    return { pct, r };
  }, [form.exit_price, form.exit_quantity, pos.entry_price, pos.quantity]);

  const handleSave = async () => {
    if (!form.exit_price || saving) return;
    setSaving(true);
    setError(null);
    try {
      await onSave({
        exit_price:    parseFloat(form.exit_price),
        exit_quantity: parseInt(form.exit_quantity) || pos.quantity,
        exit_date:     form.exit_date || today,
      });
    } catch (_) {
      setError('Erro ao encerrar. Tente novamente.');
      setSaving(false);
    }
  };

  return (
    <div className="pm-overlay" onClick={onClose}>
      <div className="pm-modal" onClick={e => e.stopPropagation()}>
        <div className="pm-header">
          <div className="pm-header-left">
            <span className="pm-ticker">{pos.ticker}</span>
            {pos.name && pos.name !== pos.ticker && <span className="pm-name">{pos.name}</span>}
          </div>
          <div className="pm-header-right">
            <span className="pm-strategy-badge" style={{ background: strat.bg, border: `1px solid ${strat.border}`, color: strat.color }}>
              {strategyLabel(pos.strategy)}
            </span>
            <span className="cart-close-badge">Encerrar posição</span>
            <button className="pm-close" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="pm-ref-strip" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="pm-ref-cell">
            <span className="pm-ref-label">Preço de entrada</span>
            <span className="pm-ref-val">{fmtBRL(pos.entry_price)}</span>
          </div>
          <div className="pm-ref-cell">
            <span className="pm-ref-label">Quantidade</span>
            <span className="pm-ref-val">{pos.quantity} ações</span>
          </div>
          <div className="pm-ref-cell">
            <span className="pm-ref-label">Preço atual</span>
            <span className="pm-ref-val pm-ref-highlight">{fmtBRL(pos.current_price)}</span>
          </div>
        </div>

        <div className="pm-body">
          <div className="pm-row-2">
            <div className="pm-field">
              <label className="pm-label">Preço de saída <span className="pm-req">*</span></label>
              <div className="pm-input-wrap">
                <span className="pm-prefix">R$</span>
                <input
                  className="pm-input"
                  type="number"
                  step="0.01"
                  placeholder="0,00"
                  value={form.exit_price}
                  onChange={e => setForm(f => ({ ...f, exit_price: e.target.value }))}
                  autoFocus
                />
              </div>
            </div>
            <div className="pm-field">
              <label className="pm-label">Quantidade vendida</label>
              <div className="pm-input-wrap">
                <input
                  className="pm-input"
                  type="number"
                  min="1"
                  style={{ paddingLeft: 12 }}
                  value={form.exit_quantity}
                  onChange={e => setForm(f => ({ ...f, exit_quantity: e.target.value }))}
                />
              </div>
            </div>
          </div>

          <div className="pm-field">
            <label className="pm-label">Data de encerramento</label>
            <div className="pm-input-wrap">
              <input
                className="pm-input"
                type="date"
                style={{ paddingLeft: 12, colorScheme: 'dark' }}
                value={form.exit_date}
                onChange={e => setForm(f => ({ ...f, exit_date: e.target.value }))}
              />
            </div>
          </div>

          {pnlPreview && (
            <div className={`pm-rr ${pnlPreview.pct >= 0 ? 'pm-rr-good' : 'pm-rr-bad'}`}>
              <div className="pm-rr-left">
                <span className="pm-rr-label">Resultado realizado</span>
                <span className="pm-rr-ratio" style={{ color: pnlPreview.pct >= 0 ? '#26a69a' : '#ef5350' }}>
                  {pnlPreview.pct >= 0 ? '+' : ''}{pnlPreview.pct.toFixed(2)}%
                </span>
              </div>
              <div className="pm-rr-right">
                <span className={`pm-rr-item ${pnlPreview.r >= 0 ? 'target' : 'stop'}`}>
                  P&amp;L <strong>{fmtSignedBRL(pnlPreview.r)}</strong>
                </span>
              </div>
            </div>
          )}

          {error && <div className="pm-error">{error}</div>}
        </div>

        <div className="pm-footer">
          <button className="pm-cancel" onClick={onClose}>Cancelar</button>
          <button
            className="pm-confirm"
            style={{ background: 'linear-gradient(135deg, #26a69a, #1a8f84)' }}
            onClick={handleSave}
            disabled={saving || !form.exit_price}
          >
            {saving ? 'Encerrando...' : 'Confirmar encerramento'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Position Card ─────────────────────────────────────────
export function PositionCard({ pos, selected, onClick, onRemove, onClose: onClosePos }) {
  const strat  = sc(pos.strategy);
  const closed = pos.status === 'closed';
  const pending = pos.status === 'pending';
  const cancelled = pos.status === 'cancelled' || pos.status === 'expired';
  const hasPnl = pos.pnl_pct != null;
  const isPos  = pos.pnl_pct > 0;
  const stopHit = !closed && !pending && !cancelled && (
    pos.stop_hit === true
    || pos.stopHit === true
    || (pos.current_price != null && pos.stop_price != null && Number(pos.current_price) < Number(pos.stop_price))
  );

  const entryMomentFmt = fmtBrtMoment(pos.entry_bar_time);
  const exitMomentFmt = fmtBrtMoment(pos.exit_bar_time);
  const entryDateFmt = pos.entry_date ? formatTradingDate(pos.entry_date) : null;
  const exitDateFmt = pos.exit_date ? formatTradingDate(pos.exit_date) : null;
  const armedDateFmt = pos.armed_date ? formatTradingDate(pos.armed_date) : null;
  const triggerPrice = pos.trigger_price ?? pos.entry_price;
  const sessionsRemaining = Number.isInteger(pos.pending_sessions_remaining)
    ? pos.pending_sessions_remaining
    : null;
  const pendingMeta = sessionsRemaining == null
    ? 'Aguardando pregão concluído'
    : `${sessionsRemaining} ${sessionsRemaining === 1 ? 'pregão restante' : 'pregões restantes'}`;

  return (
    <div
      className={`cart-pos ${(closed || cancelled) ? 'cart-pos--closed' : ''} ${pending ? 'cart-pos--pending' : ''} ${selected ? 'cart-pos--sel' : ''} ${hasPnl ? (isPos ? 'cart-pos--win' : 'cart-pos--loss') : ''}`}
      onClick={onClick}
    >
      <div className="cart-pos-bar" style={{ background: (closed || cancelled) ? '#2a3a55' : pending ? '#f0af52' : hasPnl ? (isPos ? '#26a69a' : '#ef5350') : '#2a3a55' }} />

      <div className="cart-pos-main">
        <div className="cart-pos-row1">
          <span className="cart-pos-ticker">{pos.ticker}</span>
          <span className="cart-pos-strat" style={{ color: strat.color, background: strat.bg, borderColor: strat.border }}>
            {strategyLabel(pos.strategy)}
          </span>
          {closed && <span className="cart-pos-closed-badge">encerrada</span>}
          {pending && <span className="cart-pos-closed-badge">ordem pendente</span>}
          {cancelled && <span className="cart-pos-closed-badge">ordem cancelada</span>}
          {stopHit && <span className="cart-pos-stop-hit-badge">STOP ATINGIDO</span>}
          {pos.automatic_monitoring && <span className="cart-pos-auto-badge">automática</span>}
          {pos.override === true && <span className="cart-pos-override-badge">override</span>}
        </div>
        <div className="cart-pos-name">{pos.name || pos.ticker}</div>
        <div className="cart-pos-prices">
          {(pending || cancelled) ? (
            <span className="cart-pos-entry">Gatilho {fmtBRL(triggerPrice)}</span>
          ) : (
            <span className="cart-pos-entry">{fmtBRL(pos.entry_price)}</span>
          )}
          {!pending && !cancelled && pos.current_price != null && (
            <>
              <span className="cart-pos-arrow">→</span>
              <span style={{ color: closed ? '#5e6a85' : (isPos ? '#34d399' : '#ef5350'), fontWeight: 600 }}>
                {fmtBRL(pos.current_price)}
              </span>
            </>
          )}
          <span className="cart-pos-qty">× {closed ? (pos.exit_quantity || pos.quantity) : pos.quantity}</span>
        </div>
        {closed ? (
          (exitMomentFmt || exitDateFmt || entryMomentFmt || entryDateFmt) && (
            <div className="cart-pos-stops">
              <span>
                Entrada {entryMomentFmt || entryDateFmt}
                {exitMomentFmt || exitDateFmt ? ` · Saída ${exitMomentFmt || exitDateFmt}` : ''}
              </span>
            </div>
          )
        ) : pending ? (
          <div className="cart-pos-stops">
            <span>Armada em {armedDateFmt || entryDateFmt || '—'} · {pendingMeta}</span>
            {pos.stop_price != null && <span>Stop {fmtBRL(pos.stop_price)}</span>}
          </div>
        ) : cancelled ? (
          <div className="cart-pos-stops">
            <span>{pos.cancel_reason || pos.exit_reason || 'Prazo de execução encerrado sem fill.'}</span>
          </div>
        ) : (
          <div className="cart-pos-stops">
            {(entryMomentFmt || entryDateFmt) && (
              <span>{entryMomentFmt ? `Candle de entrada ${entryMomentFmt}` : `Entrada em ${entryDateFmt}`}</span>
            )}
            {pos.stop_price != null && (
              <>
                <span>Stop {fmtBRL(pos.stop_price)}</span>
                {pos.target_price != null && pos.alvo_status?.tipo !== 'atingido' && <span className="cart-pos-target">Alvo {fmtBRL(pos.target_price)}</span>}
              </>
            )}
            {pos.alvo_status?.tipo === 'atingido' && (
              <span className="cart-pos-target-hit" data-testid="alvo-atingido" role="status">{pos.alvo_status.texto}</span>
            )}
          </div>
        )}
      </div>

      <div className="cart-pos-right">
        {pending ? (
          <div className="cart-pos-order-state">Sem capital alocado</div>
        ) : cancelled ? (
          <div className="cart-pos-order-state">Sem execução</div>
        ) : hasPnl ? (
          <>
            <div className={`cart-pos-pct ${isPos ? 'pos' : 'neg'}`}>{fmtPct(pos.pnl_pct)}</div>
                <div className={`cart-pos-pnlr ${isPos ? 'pos' : 'neg'}`}>{fmtSignedBRL(pos.pnl_r)}</div>
            {!closed && pos.change_today != null && (
              <div className={`cart-pos-today ${pos.change_today >= 0 ? 'pos' : 'neg'}`}>
                hoje {pos.change_today > 0 ? '+' : ''}{pos.change_today.toFixed(2)}%
              </div>
            )}
          </>
        ) : (
          <div className="cart-pos-noprice">sem cotação</div>
        )}
        <div className="cart-pos-actions">
          {!closed && !pending && !cancelled && (
            <button
              className={`cart-pos-close-btn ${stopHit ? 'cart-pos-close-btn--urgent' : ''}`}
              onClick={e => { e.stopPropagation(); onClosePos(); }}
              title="Encerrar posição"
            >
              {stopHit ? 'STOP · Encerrar' : 'Encerrar'}
            </button>
          )}
          <button className="cart-pos-rm" onClick={onRemove} title="Remover">✕</button>
        </div>
      </div>
    </div>
  );
}

// ── Best / Worst highlight ────────────────────────────────
function BestWorstRow({ positions }) {
  const withPnl = positions.filter(p => p.pnl_pct != null && p.pnl_r != null);
  if (withPnl.length < 2) return null;
  const best  = withPnl.reduce((b, p) => p.pnl_pct > (b?.pnl_pct ?? -Infinity) ? p : b, null);
  const worst = withPnl.reduce((b, p) => p.pnl_pct < (b?.pnl_pct ??  Infinity) ? p : b, null);
  return (
    <div className="cart-bw-row">
      <div className="cart-bw cart-bw--best">
        <span className="cart-bw-label">★ Melhor</span>
        <span className="cart-bw-ticker">{best.ticker}</span>
        <span className="cart-bw-val pos">{fmtPct(best.pnl_pct)}</span>
        <span className="cart-bw-r pos">{fmtSignedBRL(best.pnl_r)}</span>
      </div>
      <div className="cart-bw cart-bw--worst">
        <span className="cart-bw-label">▼ Pior</span>
        <span className="cart-bw-ticker">{worst.ticker}</span>
        <span className="cart-bw-val neg">{fmtPct(worst.pnl_pct)}</span>
        <span className="cart-bw-r neg">{fmtSignedBRL(worst.pnl_r)}</span>
      </div>
    </div>
  );
}

function PositionChartEmpty() {
  return (
    <div className="cart-position-chart-empty" data-testid="portfolio-position-chart-empty">
      <div className="cart-position-chart-empty-icon">⌁</div>
      <strong>Selecione uma operação</strong>
      <span>Clique em uma posição para ver o gráfico, a estratégia montada e os níveis de risco.</span>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────
function EmptyState({ onAdd }) {
  return (
    <div className="cart-empty">
       <div className="cart-empty-icon" aria-hidden="true">▦</div>
      <div className="cart-empty-title">Sua carteira está vazia</div>
      <div className="cart-empty-sub">Adicione posições teóricas para acompanhar P&L, stop e alvo em tempo real.</div>
      <button className="cart-add-btn" onClick={onAdd}>+ Adicionar primeira posição</button>
    </div>
  );
}

export function RiskSummaryCard({ summary, loading, unavailable }) {
  const percent = (number) => number == null || Number.isNaN(Number(number)) ? '—' : `${Number(number).toFixed(2)}%`;
  const estado = texto => <section className="cart-risk-card cart-risk-card--state" aria-label="Resumo de risco" data-testid="status-risk-summary">{texto}</section>;
  if (loading) return estado('Carregando resumo de risco…');
  if (unavailable) return estado('Dados de risco indisponíveis: não foi possível consultar o servidor. Tente novamente em instantes.');
  // FAT-34: sem capital operacional a API devolve só { configured: false }; antes o card mostrava apenas traços.
  if (summary?.configured === false) return estado('Configure o capital operacional para ativar o Agente de Risco (Heat, teto e drawdown).');
  const heat = summary?.heat?.totalPct == null ? null : Number(summary.heat.totalPct) * 100;
  const ceiling = summary?.limits?.heat_maximo_pct;
  const drawdown = summary?.drawdown_pct;
  const hwm = summary?.hwm;
  if (heat == null || ceiling == null || drawdown == null || hwm == null) return estado('Dados de risco indisponíveis: resposta incompleta do servidor (cotação ou capital ausente).');
  const semStop = Number(summary?.posicoes_sem_stop) || 0;
  const concentration = summary?.maior_concentracao;
  const diagnosis = Array.isArray(summary?.diagnostico_retroativo) ? summary.diagnostico_retroativo : [];
  const mode = summary?.mode || 'normal';
  const diagnosisTickers = [...new Set(diagnosis.map(item => item?.ticker).filter(Boolean))];
  return (
    <section className="cart-risk-card" aria-label="Resumo de risco" data-testid="card-risk-summary">
      <div className="cart-risk-head"><span>Agente de Risco</span><strong>{mode}</strong></div>
      <div className="cart-risk-grid">
        <div><span>Heat / teto</span><b>{percent(heat)} / {percent(ceiling)}</b></div>
        <div><span>Drawdown / HWM</span><b>{percent(drawdown)} / {fmtBRL(hwm)}</b></div>
        <div><span>Maior concentração</span><b>{typeof concentration === 'object' ? (concentration?.key || concentration?.label || concentration?.name || '—') : (concentration || '—')}</b></div>
      </div>
      <p>
        {diagnosis.length
          ? `${diagnosis.length} posição(ões) acima do risco planejado (${diagnosisTickers.join(', ')}). Revise os stops ou reduza a exposição.`
          : 'Nenhuma posição está acima do risco planejado.'}
        {semStop > 0 && ` ${semStop} posição(ões) sem stop não entram no Heat.`}
      </p>
    </section>
  );
}

// ── Main Component ────────────────────────────────────────
export default function CarteiraDashboard() {
  const [positions,   setPositions]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedId,  setSelectedId]  = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [closingPos,  setClosingPos]  = useState(null);  // position being closed
  const [showClosed,  setShowClosed]  = useState(false); // toggle closed section
  const [sortKey,     setSortKey]     = useState('pnl');
  const [strategyFilter, setStrategyFilter] = useState('all');
  const [riskSummary, setRiskSummary] = useState(null);
  const [riskLoading, setRiskLoading] = useState(true);
  const [riskUnavailable, setRiskUnavailable] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await getPortfolio();
      setPositions((data.positions || []).map(position => ({
        ...position,
        strategy: normalizeStrategyId(position.strategy),
      })));
    } catch (e) { console.error('Carteira:', e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);
  useEffect(() => {
    const loadRiskSummary = async () => {
      setRiskLoading(true);
      try {
        setRiskSummary(await getRiskSummary());
        setRiskUnavailable(false);
      } catch (_) {
        setRiskUnavailable(true);
      } finally {
        setRiskLoading(false);
      }
    };
    loadRiskSummary();
  }, []);

  const handleRemove = useCallback(async (id, e) => {
    e.stopPropagation();
    try { await removePosition(id); setPositions(p => p.filter(x => x.id !== id)); } catch (_) {}
  }, []);

  const handleAdd = useCallback(async (data) => {
    try {
      const res = await addPosition(data);
      setPositions(p => [{
        ...res.position,
        strategy: normalizeStrategyId(res.position.strategy),
      }, ...p]);
      setShowModal(false);
    } catch (error) {
      throw error;
    }
  }, []);

  const handleClosePosition = useCallback(async (id, data) => {
    try {
      const res = await closePosition(id, data);
      setPositions(p => p.map(x => x.id === id ? {
        ...x,
        ...res.position,
        strategy: normalizeStrategyId(res.position.strategy),
        status: 'closed',
      } : x));
      setClosingPos(null);
    } catch (_) {}
  }, []);

  const visiblePositions = useMemo(() => (
    strategyFilter === 'all'
      ? positions
      : positions.filter(position => (position.strategy || 'Manual') === strategyFilter)
  ), [positions, strategyFilter]);
  const open   = useMemo(() => visiblePositions.filter(p => p.status === 'open'), [visiblePositions]);
  const pending = useMemo(() => visiblePositions.filter(p => p.status === 'pending'), [visiblePositions]);
  const cancelled = useMemo(() => visiblePositions.filter(p => p.status === 'cancelled' || p.status === 'expired'), [visiblePositions]);
  const closed = useMemo(() => visiblePositions.filter(p => p.status === 'closed'), [visiblePositions]);

  const sorted = useMemo(() => {
    const list = [...open];
    if (sortKey === 'pnl')      list.sort((a, b) => (b.pnl_pct ?? -Infinity) - (a.pnl_pct ?? -Infinity));
    else if (sortKey === 'ticker')   list.sort((a, b) => a.ticker.localeCompare(b.ticker));
    else if (sortKey === 'strategy') list.sort((a, b) => (a.strategy || '').localeCompare(b.strategy || ''));
    return list;
  }, [open, sortKey]);

  const sortedClosed = useMemo(() => (
    [...closed].sort((a, b) => (b.exit_date || '').localeCompare(a.exit_date || ''))
  ), [closed]);
  const selectedPosition = useMemo(
    () => positions.find(position => position.id === selectedId) || null,
    [positions, selectedId]
  );

  const kpis = useMemo(() => {
    const invested = open.reduce((sum, position) => sum + position.entry_price * position.quantity, 0);
    const openPnl = open.reduce((sum, position) => sum + (position.pnl_r || 0), 0);
    const measuredClosed = closed.filter(position => position.pnl_r != null);
    const realizedPnl = measuredClosed.reduce((sum, position) => sum + position.pnl_r, 0);
    const winners = measuredClosed.filter(position => position.pnl_r > 0).length;
    const losers = measuredClosed.filter(position => position.pnl_r < 0).length;
    const winRate = measuredClosed.length ? winners / measuredClosed.length * 100 : null;
    return { invested, openPnl, realizedPnl, openCount: open.length, closedCount: closed.length, winRate, winners, losers };
  }, [open, closed]);

  if (loading) {
    return (
      <div className="cart-root">
        <div className="cart-loading"><div className="cart-spinner" />Carregando carteira…</div>
      </div>
    );
  }

  if (!positions.length) {
    return (
      <div className="cart-root">
        <EmptyState onAdd={() => setShowModal(true)} />
        <RiskSummaryCard summary={riskSummary} loading={riskLoading} unavailable={riskUnavailable} />
        {showModal && (
          <PortfolioModal
            ticker="VALE3" name="Ativo" strategy="Manual"
            onClose={() => setShowModal(false)} onSave={handleAdd}
          />
        )}
      </div>
    );
  }

  if (!open.length && !positions.length) {
    return (
      <div className="cart-root">
        <EmptyState onAdd={() => setShowModal(true)} />
      </div>
    );
  }

  return (
    <div className="cart-root">

      {/* ── KPI Strip ── */}
      <div className="cart-kpis">
        <div className="cart-kpi">
          <div className="cart-kpi-label">Capital em aberto</div>
          <div className="cart-kpi-value">{fmtBRL(kpis.invested)}</div>
        </div>
        <div className="cart-kpi">
          <div className="cart-kpi-label">P&amp;L em aberto</div>
          <div className={`cart-kpi-value ${kpis.openPnl >= 0 ? 'pos' : 'neg'}`}>
            {fmtSignedBRL(kpis.openPnl)}
          </div>
        </div>
        <div className="cart-kpi">
          <div className="cart-kpi-label">Resultado realizado</div>
          <div className={`cart-kpi-value ${kpis.realizedPnl >= 0 ? 'pos' : 'neg'}`}>
            {fmtSignedBRL(kpis.realizedPnl)}
          </div>
        </div>
        <div className="cart-kpi">
          <div className="cart-kpi-label">Operações encerradas</div>
          <div className="cart-kpi-value neutral">{kpis.closedCount}</div>
        </div>
        <div className="cart-kpi">
          <div className="cart-kpi-label">Win Rate</div>
          <div className={`cart-kpi-value ${kpis.winRate != null && kpis.winRate >= 50 ? 'pos' : 'neg'}`}>
            {kpis.winRate != null ? `${kpis.winRate.toFixed(0)}%` : '—'}
          </div>
        </div>
        <div className="cart-kpi cart-kpi--split">
          <div className="cart-kpi-split-item">
            <span className="cart-kpi-mini-label">Vencedoras</span>
            <span className="cart-kpi-mini-val pos">{kpis.winners}</span>
          </div>
          <div className="cart-kpi-split-div" />
          <div className="cart-kpi-split-item">
            <span className="cart-kpi-mini-label">Perdedoras</span>
            <span className="cart-kpi-mini-val neg">{kpis.losers}</span>
          </div>
        </div>
        <div className="cart-kpi cart-kpi--action">
          <button className="cart-add-btn" onClick={() => setShowModal(true)}>+ Posição</button>
          <button className="cart-refresh-btn" onClick={load}>↻</button>
        </div>
      </div>

      <StrategyEvolution
        positions={positions}
        selectedStrategy={strategyFilter}
        onSelect={setStrategyFilter}
      />
      <RiskSummaryCard summary={riskSummary} loading={riskLoading} unavailable={riskUnavailable} />

      {/* ── Body ── */}
      <div className="cart-body">

        {/* Left: position list */}
        <div className="cart-left">
          <div className="cart-left-head">
            <span className="cart-left-title">Posições</span>
            <div className="cart-sort-tabs">
              {[['pnl','P&L'],['ticker','Ticker'],['strategy','Setup']].map(([k,l]) => (
                <button key={k} className={`cart-sort-tab ${sortKey === k ? 'active' : ''}`} onClick={() => setSortKey(k)}>
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="cart-pos-list">
            {sorted.map(p => (
              <PositionCard
                key={p.id}
                pos={p}
                selected={p.id === selectedId}
                onClick={() => setSelectedId(id => id === p.id ? null : p.id)}
                onRemove={e => handleRemove(p.id, e)}
                onClose={() => setClosingPos(p)}
              />
            ))}

            {pending.length > 0 && (
              <section className="cart-orders-section" aria-label="Ordens pendentes">
                <div className="cart-orders-head">
                  <span>Ordens pendentes</span>
                  <span className="cart-closed-count">{pending.length}</span>
                </div>
                {pending.map(p => (
                  <PositionCard
                    key={p.id}
                    pos={p}
                    selected={false}
                    onClick={() => setSelectedId(null)}
                    onRemove={e => handleRemove(p.id, e)}
                    onClose={() => {}}
                  />
                ))}
              </section>
            )}

            {cancelled.length > 0 && (
              <section className="cart-orders-section cart-orders-section--cancelled" aria-label="Ordens canceladas">
                <div className="cart-orders-head">
                  <span>Ordens canceladas</span>
                  <span className="cart-closed-count">{cancelled.length}</span>
                </div>
                {cancelled.map(p => (
                  <PositionCard
                    key={p.id}
                    pos={p}
                    selected={false}
                    onClick={() => setSelectedId(null)}
                    onRemove={e => handleRemove(p.id, e)}
                    onClose={() => {}}
                  />
                ))}
              </section>
            )}

            {/* Closed positions section */}
            {sortedClosed.length > 0 && (
              <div className="cart-closed-section">
                <button
                  className="cart-closed-toggle"
                  onClick={() => setShowClosed(v => !v)}
                >
                  <span className="cart-closed-toggle-icon">{showClosed ? '▾' : '▸'}</span>
                  Encerradas
                  <span className="cart-closed-count">{sortedClosed.length}</span>
                </button>
                {showClosed && sortedClosed.map(p => (
                  <PositionCard
                    key={p.id}
                    pos={p}
                    selected={p.id === selectedId}
                    onClick={() => setSelectedId(id => id === p.id ? null : p.id)}
                    onRemove={e => handleRemove(p.id, e)}
                    onClose={() => {}}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: charts */}
        <div className="cart-right">
          <PortfolioEquityChart strategy={strategyFilter} />
          {selectedPosition ? <PortfolioPositionChart key={selectedPosition.id} position={selectedPosition} /> : <PositionChartEmpty />}
          <PnLChart positions={closed} />

          <div className="cart-right-bottom">
            <AllocationDonut positions={open} />
            <BestWorstRow positions={visiblePositions} />
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {showModal && (
        <PortfolioModal
          ticker="VALE3" name="Ativo" strategy="Manual"
          onClose={() => setShowModal(false)} onSave={handleAdd}
        />
      )}

      {/* Close Modal */}
      {closingPos && (
        <CloseModal
          pos={closingPos}
          onClose={() => setClosingPos(null)}
          onSave={data => handleClosePosition(closingPos.id, data)}
        />
      )}
    </div>
  );
}
